import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output,
  HostListener,
  ElementRef,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { AdminService } from 'src/app/services/admin.service';
import { TimelogService } from 'src/app/services/timelog.service';
import { TimerService } from 'src/app/services/timer/timer.service';

@Component({
  selector: 'app-log-time-modal',
  templateUrl: './log-time-modal.component.html',
  styleUrls: ['./log-time-modal.component.scss'],
  standalone: false,
})
export class LogTimeModalComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;
  @Input() taskId: string = '';
  @Input() projectId: string = '';
  @Input() timerId: string = ''; // ID of the timer being stopped
  @Input() task: any = null; // Full task object with project details
  @Input() data: any = null; // Timer data for pre-filling
  @Input() projectEditable: boolean = true; // Control if project field is editable
  @Input() taskEditable: boolean = true; // Control if task field is editable
  @Input() subtaskEditable: boolean = true; // Control if subtask field is editable
  @Output() visibleChange = new EventEmitter<boolean>(); // For two-way binding
  @Output() onSave = new EventEmitter<any>();
  @Output() onCancel = new EventEmitter<void>();

  logTimeForm: FormGroup;
  isSubmitting: boolean = false;
  apiUrl = environment.baseUrl;

  // Edit mode properties
  timeLogId: string | null = null;
  isEditMode: boolean = false;

  // Project members and current user
  projectMembers: any[] = [];
  currentUser: any = null;
  isLoadingMembers: boolean = false;

  // Duration presets and lock functionality
  durationPresets = [120, 60, 45, 30, 15]; // in minutes
  isDurationLocked = false;
  calculateEndFromDuration = false; // true: hrs/mins -> end time, false: times -> hrs/mins

  // Autocomplete state
  projectOptions: any[] = [];
  taskOptions: any[] = [];
  subtaskOptions: any[] = [];
  isLoadingProjects: boolean = false;
  isLoadingTasks: boolean = false;
  isLoadingSubtasks: boolean = false;
  isSubtask: boolean = false; // true when logging time for a subtask
  selectedProjectDetails: any = null; // Store fetched project details

  // Debug mode flag
  private isDebugMode: boolean = true;

  // Custom time picker state
  activeTimePicker: 'start' | 'end' | null = null;
  pickerHour: number = 12;
  pickerMinute: number = 0;
  pickerPeriod: 'AM' | 'PM' = 'AM';
  private _pickerJustOpened = false;
  readonly hours = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  readonly minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  @HostListener('document:click', ['$event'])
  onDocumentClick(e: MouseEvent): void {
    if (this._pickerJustOpened) {
      this._pickerJustOpened = false;
      return;
    }
    this.activeTimePicker = null;
  }

  openTimePicker(which: 'start' | 'end', e: MouseEvent): void {
    e.stopPropagation();
    if (this.activeTimePicker === which) {
      this.activeTimePicker = null;
      return;
    }
    this._pickerJustOpened = true;
    const val = this.logTimeForm.get(
      which === 'start' ? 'startTime' : 'endTime',
    )?.value;
    const selectedDate = this.logTimeForm.get('startTime')?.value;
    const fallback =
      selectedDate instanceof Date && !isNaN(selectedDate.getTime())
        ? new Date(selectedDate)
        : new Date();
    const d = val instanceof Date && !isNaN(val.getTime()) ? val : fallback;
    const h24 = d.getHours();
    this.pickerPeriod = h24 >= 12 ? 'PM' : 'AM';
    this.pickerHour = h24 % 12 || 12;
    this.pickerMinute = d.getMinutes();
    this.activeTimePicker = which;
  }

  selectHour(h: number): void {
    this.pickerHour = h;
    this.applyTimePicker();
  }

  selectMinute(m: number): void {
    this.pickerMinute = m;
    this.applyTimePicker();
  }

  selectPeriod(p: 'AM' | 'PM'): void {
    this.pickerPeriod = p;
    this.applyTimePicker();
  }

  private applyTimePicker(): void {
    const ctrl = this.activeTimePicker === 'start' ? 'startTime' : 'endTime';
    // Use the selected date (from startTime) as base to avoid cross-day diff bugs
    const selectedDate = this.logTimeForm.get('startTime')?.value;
    const dateBase =
      selectedDate instanceof Date && !isNaN(selectedDate.getTime())
        ? new Date(selectedDate)
        : new Date();

    let h = this.pickerHour % 12;
    if (this.pickerPeriod === 'PM') h += 12;

    const base = new Date(
      dateBase.getFullYear(),
      dateBase.getMonth(),
      dateBase.getDate(),
      h,
      this.pickerMinute,
      0,
      0,
    );

    if (this.activeTimePicker === 'end') {
      const startVal = this.logTimeForm.get('startTime')?.value;
      if (startVal instanceof Date && !isNaN(startVal.getTime())) {
        if (base.getTime() < startVal.getTime()) {
          // If end time is earlier than start time, span into next day (overnight)
          base.setDate(base.getDate() + 1);
        }
      }
    }

    this.logTimeForm.patchValue({ [ctrl]: base }, { emitEvent: false });
    if (this.activeTimePicker === 'start') this.onStartTimeChange();
    else this.onEndTimeChange();
  }

  onDateSelect(selectedDate: any): void {
    if (!selectedDate) return;
    const d = new Date(selectedDate);
    if (isNaN(d.getTime())) return;
    const y = d.getFullYear();
    const m = d.getMonth();
    const day = d.getDate();

    // Preserve hours and minutes of current startTime
    const currentStart = this.logTimeForm.get('startTime')?.value;
    let startHours = 10;
    let startMinutes = 0;
    if (currentStart instanceof Date && !isNaN(currentStart.getTime())) {
      startHours = currentStart.getHours();
      startMinutes = currentStart.getMinutes();
    }
    const newStart = new Date(y, m, day, startHours, startMinutes, 0, 0);

    // Synchronize endTime's calendar date to match the newly selected working date
    const currentEnd = this.logTimeForm.get('endTime')?.value;
    let endHours = 10;
    let endMinutes = 30;
    if (currentEnd instanceof Date && !isNaN(currentEnd.getTime())) {
      endHours = currentEnd.getHours();
      endMinutes = currentEnd.getMinutes();
    }
    let newEnd = new Date(y, m, day, endHours, endMinutes, 0, 0);

    if (newEnd.getTime() < newStart.getTime()) {
      newEnd.setDate(newEnd.getDate() + 1);
    }

    this.logTimeForm.patchValue(
      {
        startTime: newStart,
        endTime: newEnd,
      },
      { emitEvent: false },
    );

    if (this.isDurationLocked) {
      const hours = this.logTimeForm.get('durationHours')?.value || 0;
      const minutes = this.logTimeForm.get('durationMinutes')?.value || 0;
      const totalMinutes = hours * 60 + minutes;
      const calculatedEnd = new Date(newStart);
      calculatedEnd.setMinutes(calculatedEnd.getMinutes() + totalMinutes);
      this.logTimeForm.patchValue(
        { endTime: calculatedEnd },
        { emitEvent: false },
      );
    } else {
      this.calculateDurationFromTimes();
    }
  }

  onDateBlur(): void {
    const val = this.logTimeForm.get('startTime')?.value;
    if (val) {
      this.onDateSelect(val);
    }
  }

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private http: HttpClient,
    private adminService: AdminService,
    private timelogService: TimelogService,
    private timerService: TimerService,
    private elRef: ElementRef,
  ) {
    this.logTimeForm = this.fb.group({
      project: [null, Validators.required],
      task: [null, Validators.required],
      subtask: [null],
      startTime: ['', Validators.required],
      endTime: ['', Validators.required],
      durationHours: [0, [Validators.required, Validators.min(0)]],
      durationMinutes: [
        0,
        [Validators.required, Validators.min(0), Validators.max(59)],
      ],
      description: ['', Validators.required],
      isBillable: [true],
      userIds: [[]],
    });
  }

  ngOnInit(): void {
    this.loadCurrentUser();
    this.loadProjectMembers();
    this.patchFormWithTimerData();

    // Pre-fill project and task if provided via @Input
    this.preFillProjectAndTask();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When data input changes, update the form
    if (changes['data'] && !changes['data'].firstChange) {
      this.patchFormWithTimerData();
    }
    if (changes['visible'] && !changes['visible'].firstChange && this.visible) {
      this.ngOnInit();
    }

    // If timerId changes, update it
    if (changes['timerId'] && !changes['timerId'].firstChange) {
      this.timerId = changes['timerId'].currentValue;
    }

    // If projectId or taskId changes, re-fetch and pre-fill
    if (
      changes['projectId'] &&
      !changes['projectId'].firstChange &&
      this.projectId
    ) {
      this.preFillProjectAndTask();
    }
    if (changes['task'] && !changes['task'].firstChange && this.task) {
      this.preFillProjectAndTask();
    }
  }

  /**
   * Patch form with timer data (accumulated time and calculated start/end times)
   */
  private patchFormWithTimerData(): void {
    if (this.data) {
      this.logTimeForm.patchValue({
        startTime: this.data.startTime,
        endTime: this.data.endTime,
      });
      if (this.data.initialDuration) {
        // Round up seconds to next minute if > 20 seconds
        const totalSeconds = this.data.initialDuration;
        const remainingSeconds = totalSeconds % 60;
        const shouldRoundUp = remainingSeconds > 20;

        // Calculate total minutes (round up if needed)
        const totalMinutes = shouldRoundUp
          ? Math.ceil(totalSeconds / 60)
          : Math.floor(totalSeconds / 60);

        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        this.logTimeForm.patchValue({
          durationHours: hours,
          durationMinutes: minutes,
        });
      }

      // If timerId is passed in data but not input, use it
      if (this.data.timerId && !this.timerId) {
        this.timerId = this.data.timerId;
      }
    }
  }

  /**
   * Pre-fill project and task autocomplete fields if provided via @Input
   */
  private preFillProjectAndTask(): void {
    if (this.isDebugMode) {
      console.log('🚀 preFillProjectAndTask called');
      console.log('   projectId:', this.projectId);
      console.log('   taskId:', this.taskId);
      console.log('   task:', this.task);
    }

    // If both projectId and task provided, pre-fill both autocompletes
    if (this.projectId && this.task) {
      if (this.isDebugMode)
        console.log('📋 Case 1: Both projectId and task provided');
      this.logTimeForm.patchValue(
        {
          project: {
            _id: this.projectId,
            name: this.task.projectId?.name || this.projectName,
          },
          task: {
            _id: this.taskId,
            title: this.task.title || this.taskName,
            uniqueTaskID: this.task.uniqueTaskID || '',
          },
        },
        { emitEvent: false },
      );
    } else if (this.projectId) {
      if (this.isDebugMode) {
        console.log(
          '📋 Case 2: Only projectId provided - fetching project details',
        );
      }
      // Only projectId provided - fetch project details to pre-fill
      this.fetchAndPreFillProject(this.projectId);
    } else {
      if (this.isDebugMode) console.log('⚠️ No projectId or task provided');
    }
  }

  /**
   * Fetch project details and pre-fill project autocomplete
   */
  private fetchAndPreFillProject(projectId: string): void {
    if (this.isDebugMode)
      console.log(
        '🔍 fetchAndPreFillProject called with projectId:',
        projectId,
      );

    this.adminService.getProjectById(projectId).subscribe({
      next: (res: any) => {
        if (this.isDebugMode) console.log('✅ getProjectById response:', res);

        // Handle nested response structure: data.project
        if (res?.data?.project) {
          const projectData = res.data.project;

          // Store project details
          this.selectedProjectDetails = projectData;
          if (this.isDebugMode) {
            console.log(
              '📦 Stored project details:',
              this.selectedProjectDetails,
            );
          }

          // Pre-fill project autocomplete with correct structure
          const projectObj = {
            _id: projectData._id,
            name: projectData.name,
          };
          if (this.isDebugMode)
            console.log('🎯 Pre-filling project with:', projectObj);

          this.logTimeForm.patchValue(
            {
              project: projectObj,
            },
            { emitEvent: false },
          );

          if (this.isDebugMode) {
            console.log(
              '✅ Form patched. Current project value:',
              this.logTimeForm.get('project')?.value,
            );
          }
        } else {
          if (this.isDebugMode)
            console.warn('⚠️ Invalid response structure:', res);
        }
      },
      error: (err) => {
        console.error('❌ Error fetching project for pre-fill:', err);
      },
    });
  }

  /**
   * Autocomplete method for projects
   */
  autoCompleteProjects(event: any): void {
    const query = event.query || '';
    this.isLoadingProjects = true;

    this.adminService.autoCompleteProjects({ q: query }).subscribe({
      next: (res: any) => {
        if (res?.success && res?.data) {
          this.projectOptions = res.data;
        }
        this.isLoadingProjects = false;
      },
      error: (err) => {
        console.error('Error fetching projects:', err);
        this.isLoadingProjects = false;
      },
    });
  }

  /**
   * Autocomplete method for tasks (filtered by selected project)
   */
  autoCompleteTasks(event: any): void {
    const selectedProject = this.logTimeForm.get('project')?.value;
    if (!selectedProject?._id) {
      this.taskOptions = [];
      return;
    }

    const query = event.query || '';
    this.isLoadingTasks = true;

    this.adminService
      .autoCompleteTasks({
        q: query,
        projectId: selectedProject._id,
      })
      .subscribe({
        next: (res: any) => {
          if (res?.success && res?.data) {
            this.taskOptions = res.data;
          }
          this.isLoadingTasks = false;
        },
        error: (err) => {
          console.error('Error fetching tasks:', err);
          this.isLoadingTasks = false;
        },
      });
  }

  /**
   * Handle project selection change
   */
  onProjectChange(event: any): void {
    // Clear task and subtask selection when project changes
    this.logTimeForm.patchValue(
      { task: null, subtask: null },
      { emitEvent: false },
    );
    this.taskOptions = [];
    this.subtaskOptions = [];
    this.isSubtask = false;

    // Reload project members for the new project
    if (event?._id) {
      this.projectId = event._id;
      this.loadProjectMembers();
    }
  }

  /**
   * Handle task selection change — load subtasks for selected task
   */
  onTaskChange(event: any): void {
    this.logTimeForm.patchValue({ subtask: null }, { emitEvent: false });
    this.subtaskOptions = [];
    this.isSubtask = false;
  }

  /**
   * Autocomplete method for subtasks (filtered by selected task)
   */
  autoCompleteSubtasks(event: any): void {
    const selectedTask = this.logTimeForm.get('task')?.value;
    if (!selectedTask?._id) {
      this.subtaskOptions = [];
      return;
    }
    const query = event.query || '';
    this.isLoadingSubtasks = true;
    this.adminService.getSubtasksByTaskId(selectedTask._id).subscribe({
      next: (res: any) => {
        const all: any[] = res?.data || [];
        this.subtaskOptions = query
          ? all.filter((s: any) =>
              s.title?.toLowerCase().includes(query.toLowerCase()),
            )
          : all;
        this.isLoadingSubtasks = false;
      },
      error: () => {
        this.isLoadingSubtasks = false;
      },
    });
  }

  /**
   * Load current user from localStorage and auto-select in form
   */
  loadCurrentUser(): void {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      this.currentUser = JSON.parse(userStr);
      // Auto-select current user
      const currentUserIds = this.logTimeForm.get('userIds')?.value || [];
      if (!currentUserIds.includes(this.currentUser._id)) {
        this.logTimeForm.patchValue({
          userIds: [this.currentUser._id, ...currentUserIds],
        });
      }
    }
  }

  /**
   * Load project members for collaborative logging
   */
  loadProjectMembers(): void {
    const projectIdToUse =
      this.projectId || this.task?.projectId?._id || this.task?.projectId;

    if (!projectIdToUse) {
      console.warn('No project ID available to load members');
      return;
    }

    this.isLoadingMembers = true;
    this.adminService.projectteamMembers(projectIdToUse).subscribe({
      next: (res: any) => {
        if (res.data) {
          // console.log('projectMembers', res.data);

          // Filter out current user from the list (they're already auto-selected)
          this.projectMembers = res.data.map((member: any) => {
            const u = member.userId;
            return {
              ...u,
              fullName:
                u.fullName ||
                `${u.firstName || ''} ${u.lastName || ''}`.trim() ||
                u.userName ||
                'User',
              pivot: { ...member, userId: u._id },
            };
          });
          // .filter(
          //   (member: any) => member._id !== this.currentUser?._id
          // )

          console.log('projectMembers', this.projectMembers);
        }
        this.isLoadingMembers = false;
      },
      error: (error: any) => {
        console.error('Error loading project members:', error);
        this.toastr.error('Failed to load project members');
        this.isLoadingMembers = false;
      },
    });
  }

  /**
   * Handle user selection from user-selector component
   */
  onUsersSelected(userIds: string[]): void {
    this.logTimeForm.patchValue({ userIds });
  }

  get f() {
    return this.logTimeForm.controls;
  }

  /**
   * Calculate duration from start/end times (when lock is DISABLED)
   */
  calculateDurationFromTimes(): void {
    const start = this.logTimeForm.get('startTime')?.value;
    const end = this.logTimeForm.get('endTime')?.value;

    if (start && end) {
      const startDate = new Date(start);
      let endDate = new Date(end);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return;

      // Ensure endDate is anchored to the exact calendar date of startDate
      const sameDayEnd = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate(),
        endDate.getHours(),
        endDate.getMinutes(),
        0,
        0,
      );

      // If end time of day is before start time of day, span into next day (e.g. overnight 11 PM to 1 AM)
      if (sameDayEnd.getTime() < startDate.getTime()) {
        sameDayEnd.setDate(sameDayEnd.getDate() + 1);
      }

      if (endDate.getTime() !== sameDayEnd.getTime()) {
        endDate = sameDayEnd;
        this.logTimeForm.patchValue(
          { endTime: sameDayEnd },
          { emitEvent: false },
        );
      }

      const diffMs = endDate.getTime() - startDate.getTime();
      if (diffMs >= 0) {
        const totalMinutes = Math.floor(diffMs / 60000);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        this.logTimeForm.patchValue(
          {
            durationHours: hours,
            durationMinutes: minutes,
          },
          { emitEvent: false },
        );
      }
    }
  }

  /** Format a Date to 12h display string e.g. "10:02 AM" */
  private dateToTimeStr(val: any): string {
    if (!val) return '';
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }

  /** Parse typed time string — supports "10:02 AM", "10:02 am", "10:02", "1002" */
  get startTimeStr(): string {
    return this.dateToTimeStr(this.logTimeForm.get('startTime')?.value);
  }
  get endTimeStr(): string {
    return this.dateToTimeStr(this.logTimeForm.get('endTime')?.value);
  }

  /**
   * Called when start time changes
   */
  onStartTimeChange(): void {
    if (this.isDurationLocked) {
      // Lock ENABLED: Adjust end time to maintain duration
      const start = this.logTimeForm.get('startTime')?.value;
      const hours = this.logTimeForm.get('durationHours')?.value || 0;
      const minutes = this.logTimeForm.get('durationMinutes')?.value || 0;

      if (start) {
        const totalMinutes = hours * 60 + minutes;
        const newEndTime = new Date(start);
        newEndTime.setMinutes(newEndTime.getMinutes() + totalMinutes);

        this.logTimeForm.patchValue(
          {
            endTime: newEndTime,
          },
          { emitEvent: false },
        );
      }
    } else {
      // Lock DISABLED: Recalculate duration from times
      this.calculateDurationFromTimes();
    }
  }

  /**
   * Called when end time changes
   */
  onEndTimeChange(): void {
    if (this.isDurationLocked) {
      // Lock ENABLED: Adjust start time to maintain duration
      const end = this.logTimeForm.get('endTime')?.value;
      const hours = this.logTimeForm.get('durationHours')?.value || 0;
      const minutes = this.logTimeForm.get('durationMinutes')?.value || 0;

      if (end) {
        const totalMinutes = hours * 60 + minutes;
        const newStartTime = new Date(end);
        newStartTime.setMinutes(newStartTime.getMinutes() - totalMinutes);

        this.logTimeForm.patchValue(
          {
            startTime: newStartTime,
          },
          { emitEvent: false },
        );
      }
    } else {
      // Lock DISABLED: Recalculate duration from times
      this.calculateDurationFromTimes();
    }
  }

  /**
   * Called when duration hours/minutes change
   */
  onDurationChange(): void {
    // When duration changes manually, update end time
    const start = this.logTimeForm.get('startTime')?.value;
    const hours = this.logTimeForm.get('durationHours')?.value || 0;
    const minutes = this.logTimeForm.get('durationMinutes')?.value || 0;

    if (start) {
      const totalMinutes = hours * 60 + minutes;
      const newEndTime = new Date(start);
      newEndTime.setMinutes(newEndTime.getMinutes() + totalMinutes);

      this.logTimeForm.patchValue(
        {
          endTime: newEndTime,
        },
        { emitEvent: false },
      );
    }
  }

  /**
   * Quick duration selection - sets hours/minutes
   * Respects lock state: updates end time when setting duration
   */
  setQuickDuration(minutes: number): void {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    this.logTimeForm.patchValue({
      durationHours: hours,
      durationMinutes: mins,
    });

    // Trigger the same logic as manual duration change
    this.onDurationChange();
  }

  /**
   * Toggle duration lock state
   */
  toggleDurationLock(): void {
    this.isDurationLocked = !this.isDurationLocked;
  }

  /**
   * Navigate to task page (placeholder for now)
   */
  navigateToTask(): void {
    if (this.taskId) {
      // TODO: Implement navigation to task page
      // this.router.navigate(['/tasks', this.taskId]);
      console.log('Navigate to task:', this.taskId);
    }
  }

  onSubmit(): void {
    if (this.logTimeForm.invalid) {
      this.logTimeForm.markAllAsTouched();
      // Show specific error messages
      if (!this.logTimeForm.get('project')?.value) {
        this.toastr.error('Please select a project');
        return;
      }
      if (!this.logTimeForm.get('task')?.value) {
        this.toastr.error('Please select a task');
        return;
      }
      if (!this.logTimeForm.get('description')?.value) {
        this.toastr.error('Please enter a description');
        return;
      }
      this.toastr.error('Please fill in all required fields');
      return;
    }

    this.isSubmitting = true;
    const formValue = this.logTimeForm.value;

    // Get selected project and task from form controls
    const selectedProject = formValue.project;
    const selectedTask = formValue.task;

    // Calculate total duration in seconds
    const totalSeconds =
      formValue.durationHours * 3600 + formValue.durationMinutes * 60;

    if (totalSeconds === 0) {
      this.toastr.error('Duration cannot be 0');
      this.isSubmitting = false;
      return;
    }

    const startDate = new Date(formValue.startTime);
    let endDate = new Date(formValue.endTime);
    if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
      const sameDayEnd = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate(),
        endDate.getHours(),
        endDate.getMinutes(),
        0,
        0,
      );
      if (sameDayEnd.getTime() < startDate.getTime()) {
        sameDayEnd.setDate(sameDayEnd.getDate() + 1);
      }
      endDate = sameDayEnd;
    }

    const payload = {
      taskId: selectedTask?._id || this.taskId,
      subtaskId: formValue.subtask?._id || null,
      projectId: selectedProject?._id || this.projectId,
      date: startDate,
      startTime: startDate,
      endTime: endDate,
      duration: totalSeconds,
      description: formValue.description,
      isBillable: formValue.isBillable,
      userIds: formValue.userIds,
    };

    // Edit mode: Update existing time log
    if (this.isEditMode && this.timeLogId) {
      this.timelogService.updateTimeLog(this.timeLogId, payload).subscribe({
        next: (res: any) => {
          console.log(
            '[LogTimeModal] Time log updated successfully. Data:',
            res.data,
          );
          console.log(
            '[LogTimeModal] Description in response:',
            res.data?.description,
          );
          this.toastr.success('Time log updated successfully');
          this.isSubmitting = false;
          this.onSave.emit(res.data);
          this.close();
        },
        error: (err) => {
          console.error('Error updating time log:', err);
          this.toastr.error(err.error?.message || 'Failed to update time log');
          this.isSubmitting = false;
        },
      });
    }
    // Create mode: Create new time log
    else {
      this.timelogService.createTimeLog(payload).subscribe({
        next: (res: any) => {
          const userCount = payload.userIds?.length || 1;
          const message =
            userCount > 1
              ? `Time logged successfully for ${userCount} team members`
              : 'Time logged successfully';
          this.toastr.success(message);

          // Stop the timer if timerId is available
          if (this.timerId) {
            this.timerService.stopTimer(this.timerId);
          }

          this.isSubmitting = false;
          this.onSave.emit(res.data);
          this.close();
        },
        error: (err) => {
          console.error('Error logging time:', err);
          this.toastr.error(err.error?.message || 'Failed to log time');
          this.isSubmitting = false;
        },
      });
    }
  }

  close(): void {
    this.logTimeForm.reset({
      durationHours: 0,
      durationMinutes: 0,
      isBillable: true,
      userIds: this.currentUser ? [this.currentUser._id] : [],
    });
    this.isSubtask = false;
    this.subtaskOptions = [];
    this.visible = false;
    this.visibleChange.emit(false); // Emit for two-way binding
    this.isEditMode = false;
    this.timeLogId = null;
    this.onCancel.emit();
  }

  /**
   * Open modal manually with custom data
   * Usage: this.logTimeModal.openModalManual(taskId, { startTime: x, durationHours: y, isBillable: true })
   */
  openModalManual(
    taskId: string,
    formData?: Partial<{
      startTime: Date | string;
      endTime: Date | string;
      durationHours: number;
      durationMinutes: number;
      description: string;
      isBillable: boolean;
      userIds: string[];
      subtaskId: string;
      subtask: any;
      parentTask: any;
      projectId: string;
    }>,
  ): void {
    this.taskId = taskId;
    this.visible = true;

    // Set projectId if provided
    if (formData?.projectId) {
      this.projectId = formData.projectId;
      this.loadProjectMembers();
    }

    // If a subtask is provided, mark as subtask mode
    if (formData?.subtask) {
      this.isSubtask = true;
    }

    // Patch form with provided data
    if (formData) {
      const patchData: any = {};

      if (formData.startTime !== undefined)
        patchData.startTime = formData.startTime;
      if (formData.endTime !== undefined) patchData.endTime = formData.endTime;
      if (formData.durationHours !== undefined)
        patchData.durationHours = formData.durationHours;
      if (formData.durationMinutes !== undefined)
        patchData.durationMinutes = formData.durationMinutes;
      if (formData.description !== undefined)
        patchData.description = formData.description;
      if (formData.isBillable !== undefined)
        patchData.isBillable = formData.isBillable;
      if (formData.userIds !== undefined) patchData.userIds = formData.userIds;

      // Pre-fill parent task when logging for a subtask
      if (formData.parentTask) {
        patchData.task = {
          _id: formData.parentTask._id,
          title: formData.parentTask.title,
          uniqueTaskID: formData.parentTask.uniqueTaskID || '',
        };
      }

      // Pre-fill subtask
      if (formData.subtask) {
        patchData.subtask = {
          _id: formData.subtask._id,
          title: formData.subtask.title,
        };
      }

      this.logTimeForm.patchValue(patchData);

      // If duration is provided but endTime is not, calculate endTime
      if (
        (formData.durationHours !== undefined ||
          formData.durationMinutes !== undefined) &&
        formData.startTime &&
        !formData.endTime
      ) {
        this.onDurationChange();
      }

      // If startTime and endTime are provided but duration is not, calculate duration
      if (
        formData.startTime &&
        formData.endTime &&
        formData.durationHours === undefined &&
        formData.durationMinutes === undefined
      ) {
        this.calculateDurationFromTimes();
      }
    }

    // Pre-fill project after patching (uses this.projectId set above)
    this.preFillProjectAndTask();
  }

  /**
   * Open modal in edit mode with existing time log data
   * Usage: this.logTimeModal.openModalForEdit(timeLogId, taskId, projectId, timeLogData)
   */
  openModalForEdit(
    timeLogId: string,
    taskId: string,
    projectId: string,
    timeLogData: any,
  ): void {
    this.timeLogId = timeLogId;
    this.taskId = taskId;
    this.projectId = projectId;
    this.isEditMode = true;
    this.visible = true;

    // Load project members for the project
    this.loadProjectMembers();

    // Convert duration from seconds to hours and minutes
    const totalMinutes = Math.floor(timeLogData.duration / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    // Extract user IDs from the time log
    const userIds = timeLogData.userId
      ? [timeLogData.userId._id || timeLogData.userId]
      : [];

    // Construct project and task objects for patching
    const projectPatch = timeLogData.projectId?._id
      ? {
          _id: timeLogData.projectId._id,
          name: timeLogData.projectId.name || this.projectName,
        }
      : {
          _id: timeLogData.projectId || this.projectId,
          name: this.projectName,
        };

    const taskPatch = timeLogData.taskId?._id
      ? {
          _id: timeLogData.taskId._id,
          title: timeLogData.taskId.title || this.taskName,
          uniqueTaskID: timeLogData.taskId.uniqueTaskID || '',
        }
      : {
          _id: timeLogData.taskId || this.taskId,
          title: this.taskName,
          uniqueTaskID: '',
        };

    // Patch form with time log data including project and task objects
    this.logTimeForm.patchValue({
      project: projectPatch,
      task: taskPatch,
      startTime: new Date(timeLogData.startTime),
      endTime: new Date(timeLogData.endTime),
      durationHours: hours,
      durationMinutes: minutes,
      description: timeLogData.description || '',
      isBillable: timeLogData.isBillable,
      userIds: userIds,
    });

    // If project name is still "Unknown Project", trigger fetch
    if (projectPatch.name === 'Unknown Project' && this.projectId) {
      this.fetchAndPreFillProject(this.projectId);
    }
  }

  /**
   * Get display name for task/project context
   */
  get taskName(): string {
    return this.task?.title || 'Unknown Task';
  }

  get projectName(): string {
    return this.task?.projectId?.name || 'Unknown Project';
  }

  /**
   * Get selected user count for footer display
   */
  get selectedUserCount(): number {
    const userIds = this.logTimeForm.get('userIds')?.value || [];
    return userIds.length;
  }
}
